<template>
  <Passport
    ref="passportRef"
    :open="open"
    @update:open="handleUpdateOpen"
    type="asset-groups"
    :item="_asset_group"
    @update="handleUpdate"
    @update:item="updateAssetGroup"
    @reload:item="reloadAssetGroup"
    @delete="handleDelete"
  />
</template>

<script setup>
import axios from "axios";
import route from "ziggy-js";
import { inject, ref } from "vue";
import { Passport } from "@/SharedComponents/Passport";
import { toast } from "@/SharedComponents/Toaster";
import { useI18n } from "vue-i18n";

const emit = defineEmits(["close"]);

const passportRef = ref(null);

const props = defineProps({
  asset_group: { type: Object, required: true },
});

const open = ref(true);
const _asset_group = ref(props.asset_group);

function handleUpdateOpen(value) {
  if (!value) {
    open.value = false;
    setTimeout(() => {
      emit("close");
    }, 100);
  }
}

const busy = ref(false);

const { t } = useI18n();
const { hasPermission, permissions } = inject("permissions");
const emitter = inject("emitter");

function handleUpdate({ data, callback = () => null }) {
  if (busy.value) return;

  if (!hasPermission(permissions.updateAsset, props.asset_group.tenant.id)) {
    toast.error(t("toasts.no_permission.asset_groups.update"));
    return;
  }

  let parameters = {
    id: _asset_group.value.id,
  };

  let url = "api.asset_groups.update";

  if ("name" in data) {
    parameters.name = data.name;
  }

  if ("group" in data) {
    url = "api.asset_groups.move";
    parameters.parent_group_id = data.group ? data.group.id : data.group;
  }

  busy.value = true;
  axios
    .put(route(url), parameters)
    .then(() => {
      toast.success(t("toasts.asset_groups.update.success"));
      updateAssetGroup({ item: data });
      callback(true);
    })
    .catch((err) => {
      if (err.response?.status === 422) {
        toast.error(err.response?.data.message);
      } else {
        toast.error(t("toasts.asset_groups.update.error"));
      }
      callback(false);
    })
    .finally(() => {
      busy.value = false;
    });
}

function updateAssetGroup({ item, callback = () => null }) {
  _asset_group.value = { ..._asset_group.value, ...item };
  emitter.emit("reload-asset-groups");

  reloadAssetGroup();
  callback();
}

function handleDelete() {
  if (busy.value) return;

  if (!hasPermission(permissions.deleteAsset, props.asset_group.tenant.id)) {
    toast.error(t("toasts.no_permission.assets.delete"));
    return;
  }

  busy.value = true;
  axios
    .delete(route("api.asset_groups.delete", [props.asset_group]))
    .then((res) => {
      toast.success(t("toasts.asset_groups.delete.success"));
      emitter.emit("remove-assets-data", { data: [res.data.id] });
      emitter.emit("reload-asset-groups");
      passportRef.value?.close();
    })
    .catch(() => {
      toast.error(t("toasts.asset_groups.delete.error"));
    })
    .finally(() => {
      busy.value = false;
    });
}

function reloadAssetGroup() {
  axios
    .get(route("api.asset_groups.show", [_asset_group.value.id]))
    .then((response) => {
      _asset_group.value = response.data.data;
    });
}
</script>
